import { useState } from 'react';
import { IconCamera, IconCheck, IconPlus } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { PageHeader } from '@hana/shell';
import { Modal, StateView, useToast, type ScreenState } from '@hana/ui';
import {
  FUEL_BRANDS, FUEL_PRICE, INDEX, MAINT, REPORT0, REPORT_ITEMS, VEHICLE,
  fmtVnd, fuelCost, odoOk, parseLit, reportBtn, reportCount, type Range,
} from './vehicleData';
import './vehicle.i18n';
import s from './vehicle.module.css';

const STATES: ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];
function forcedState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return STATES.includes(v as ScreenState) ? (v as ScreenState) : 'ready';
}

/** Mint-Mov-Phone3 khung 2 "Xe của tôi" (giả lập, giữ trong bộ nhớ, toast có Hoàn tác). */
export function Vehicle() {
  const state = forcedState();
  const t = useT();
  const toast = useToast();
  const [odo, setOdo] = useState<{ v: string; fresh: boolean }>({ v: VEHICLE.odo0, fresh: false });
  const [rep, setRep] = useState<Record<string, boolean>>({ ...REPORT0 });
  const [sent, setSent] = useState(false);
  const [rng, setRng] = useState<Range>('7 ngày');
  const [sheet, setSheet] = useState<'odo' | 'fuel' | null>(null);
  const [fv, setFv] = useState({ odo: '', photo: false, brand: '', lit: '' });
  const [fErr, setFErr] = useState(false);
  const n = reportCount(rep);
  const lit = parseLit(fv.lit);
  const open = (w: 'odo' | 'fuel') => { setFv({ odo: '', photo: false, brand: '', lit: '' }); setFErr(false); setSheet(w); };

  const sendOdo = () => {
    if (!odoOk(fv.odo, fv.photo)) { setFErr(true); return; }
    const prev = odo;
    setOdo({ v: fv.odo, fresh: true });
    setSheet(null);
    toast({ msg: t('mvv.odoSent', { v: fv.odo }), undo: () => setOdo(prev) });
  };
  const saveFuel = () => {
    if (!fv.brand || !lit) { setFErr(true); return; }
    setSheet(null);
    toast({ msg: t('mvv.fuelSaved', { lit }) });
  };
  const sendRep = () => {
    if (sent) { toast({ msg: t('mvv.waiting') }); return; }
    if (n < REPORT_ITEMS.length) { toast({ msg: t('mvv.need8') }); return; }
    setSent(true);
    toast({ msg: t('mvv.repSent'), undo: () => setSent(false) });
  };
  const row = (k: string, v: string, tone?: 'ok' | 'warn', act?: { label: string; on: () => void }) => (
    <div key={k} className={s.row}>
      {tone && <span className={s.dot} data-t={tone} aria-hidden="true" />}
      <div><div className={s.k}>{k}</div><div className={s.v}>{v}</div></div>
      {act && <button type="button" className={s.act} onClick={act.on}>{act.label}</button>}
    </div>
  );

  return (
    <>
      <PageHeader title={t('mvv.title')} />
      <div className={s.page}>
        {state === 'error' && <div role="status" className={s.net}><b>{t('mvv.netB')}</b>{t('mvv.netT')}</div>}
        {state === 'noperm' && <div role="status" className={s.net}><b>{t('mvv.nopB')}</b>{t('mvv.nopT')}</div>}
        <StateView
          state={state === 'error' || state === 'noperm' || state === 'empty' ? 'ready' : state}
          skeleton={<>{[0, 1, 2, 3].map((i) => <div key={i} className={s.sk} aria-hidden="true" />)}</>}
        >
          <section className={s.card} aria-label={`${VEHICLE.plate} · ${VEHICLE.model}`}>
            <div className={s.hd}><b>{VEHICLE.plate} · {VEHICLE.model}</b><span>{VEHICLE.own}</span></div>
            {VEHICLE.rows.map((r) => row(r.k, r.v, 'tone' in r ? r.tone : undefined))}
            {row('ODO', `${odo.v} km · ${odo.fresh ? t('mvv.fresh') : t('mvv.gps')}`, undefined, { label: t('mvv.update'), on: () => open('odo') })}
          </section>

          <section className={s.card} aria-label={t('mvv.report')}>
            <div className={s.hd}><b>{t('mvv.report')}</b><span data-t={sent ? 'ok' : 'warn'}>{sent ? t('mvv.sent') : t('mvv.photos', { n })}</span></div>
            <div className={s.grid}>
              {REPORT_ITEMS.map((k) => (
                <button key={k} type="button" className={s.cell} data-on={rep[k] ? '1' : '0'} aria-pressed={!!rep[k]}
                  onClick={() => (sent ? toast({ msg: t('mvv.repDone') }) : setRep((r) => ({ ...r, [k]: !r[k] })))}>
                  {rep[k] ? <IconCheck size={18} stroke={1.8} aria-hidden="true" /> : <IconPlus size={18} stroke={1.8} aria-hidden="true" />}{k}
                </button>
              ))}
            </div>
            <button type="button" className={s.btn} disabled={sent || n < REPORT_ITEMS.length} onClick={sendRep}>{sent ? t('mvv.sentWait') : reportBtn(n)}</button>
          </section>

          <section className={s.card} aria-label={t('mvv.maint')}>
            <b className={s.hd}>{t('mvv.maint')}</b>
            {MAINT.map((m) => row(m.k, m.v, m.tone))}
          </section>

          <section className={s.card} aria-label={t('mvv.index')}>
            <div className={s.hd}><b>{t('mvv.index')}</b><span>{rng === '7 ngày' ? t('mvv.d7') : t('mvv.d30')}</span></div>
            {INDEX[rng].map(([k, v]) => row(k, v))}
            <button type="button" className={s.soft} onClick={() => setRng(rng === '7 ngày' ? '30 ngày' : '7 ngày')}>{rng === '7 ngày' ? t('mvv.see30') : t('mvv.see7')}</button>
          </section>

          <section className={s.card} aria-label={t('mvv.fuel')}>
            <b className={s.hd}>{t('mvv.fuel')}</b>
            {row(t('mvv.lastFuel'), t('mvv.lastFuelV'))}
            <button type="button" className={s.btn} onClick={() => open('fuel')}>{t('mvv.fuelBtn')}</button>
          </section>
        </StateView>
      </div>

      <Modal
        open={sheet === 'odo'}
        onOpenChange={(o) => !o && setSheet(null)}
        title={t('mvv.odoTitle')}
        footer={<button type="button" className={s.btn} style={{ width: '100%' }} onClick={sendOdo}>{t('mvv.odoSend')}</button>}
      >
        <p className={s.hint}>{t('mvv.odoHint')}</p>
        <label className={s.field}><span>{t('mvv.odoKm')}</span><input inputMode="numeric" placeholder="186.420" value={fv.odo} onChange={(e) => setFv({ ...fv, odo: e.target.value })} />{fErr && !fv.odo.trim() && <span className={s.err}>{t('mvv.odoErr')}</span>}</label>
        <button type="button" className={s.soft} style={{ width: '100%' }} onClick={() => setFv({ ...fv, photo: true })}><IconCamera size={18} stroke={1.8} aria-hidden="true" />{fv.photo ? t('mvv.shotDone') : t('mvv.shot')}</button>
        {fErr && !fv.photo && <span className={s.err}>{t('mvv.shot')}.</span>}
      </Modal>

      <Modal
        open={sheet === 'fuel'}
        onOpenChange={(o) => !o && setSheet(null)}
        title={t('mvv.fuelTitle')}
        footer={<button type="button" className={s.btn} style={{ width: '100%' }} onClick={saveFuel}>{t('mvv.save')}</button>}
      >
        <p className={s.hint}>{t('mvv.fuelHint')}</p>
        <label className={s.field}><span>{t('mvv.brand')}</span>
          <select value={fv.brand} onChange={(e) => setFv({ ...fv, brand: e.target.value })}><option value="">—</option>{FUEL_BRANDS.map((b) => <option key={b}>{b}</option>)}</select>
          {fErr && !fv.brand && <span className={s.err}>{t('mvv.brandErr')}</span>}
        </label>
        <label className={s.field}><span>{t('mvv.lit')}</span><input inputMode="decimal" placeholder="180" value={fv.lit} onChange={(e) => setFv({ ...fv, lit: e.target.value })} />
          <span className={s.hint} data-t={lit ? 'ok' : undefined}>{lit ? t('mvv.auto', { cost: fmtVnd(fuelCost(lit)), price: fmtVnd(FUEL_PRICE) }) : t('mvv.priceNote', { price: fmtVnd(FUEL_PRICE) })}</span>
          {fErr && !lit && <span className={s.err}>{t('mvv.litErr')}</span>}
        </label>
      </Modal>
    </>
  );
}
